import type { Ref } from 'vue';
import { nextTick, reactive, shallowRef } from 'vue';
import type { AdminFormApi } from './admin-api';
import type { UserEntryViewMeta } from '@cognitoforms/types/server-types/core/services/user-entry-view-meta';
import { useFormLightbox } from './use-form-lightbox';
import { websocketConnection } from 'src/utilities/websocket/websocket-connection-factory';
import { globalStore } from 'src/stores/global-store';
import type VueRouter from 'vue-router';
import type { Route } from 'vue-router';
import { WebSocketGroup } from '@cognitoforms/types/server-types/core/services/web-socket-group';

export interface FormLightboxState {
	submitted: boolean,
	currentView: UserEntryViewMeta,
	afterSubmitView: UserEntryViewMeta,
	destinationViewReady: Promise<void>,
	afterCloseRoute: Route,
};

const router = shallowRef<VueRouter>(null);
const state = reactive<FormLightboxState>({
	submitted: false,
	currentView: null,
	afterSubmitView: null,
	destinationViewReady: null,
	afterCloseRoute: null
});

export const useFormViewLightbox = (adminApiRef: Ref<AdminFormApi>, destroyAdminApi: boolean) => {
	const { openFormLightbox, closeFormLightbox, adminApi } = useFormLightbox(adminApiRef, destroyAdminApi);

	const onBeforeLightboxSubmit = () => {
		// Skip if there is no after submit view id or if the after submit view is already set
		if (!state.currentView?.AfterSubmitViewId)
			return;

		if (state.afterSubmitView) {
			state.destinationViewReady = Promise.resolve();
			return;
		}

		const formId = state.currentView.Id.split('-')[0];

		state.destinationViewReady = new Promise<void>((resolve) => {
			websocketConnection.waitForEvent('Form', formId, WebSocketGroup.User, 5000)
				.then(() => nextTick(() => {
					state.afterSubmitView = globalStore.forms[formId].EntryViews.find(
						v => v.Id === state.currentView.AfterSubmitViewId
					);
					resolve();
				}))
				.catch(() => resolve());
		});
	};

	const onLightboxSubmit = (msg: string) => {
		state.submitted = true;
	};

	const onLightboxClose = async () => {
		const formId = state.currentView.Id.split('-')[0];
		let destinationRoute = state.afterCloseRoute;

		// Handle after submit destination view
		if (!!state.currentView?.AfterSubmitViewId && state.submitted) {
			// Wait for the destination view to be ready before navigating
			if (!state.afterSubmitView && state.destinationViewReady)
				await state.destinationViewReady;

			if (state.afterSubmitView) {
				const viewNumber = state.afterSubmitView.Id.split('-')[1];

				destinationRoute = router.value.resolve({
					name: 'entries',
					params: {
						orgcode: globalStore.organization.Code,
						formUrlName: globalStore.forms[formId].UrlName,
						entryView: `${viewNumber}-${state.afterSubmitView.InternalName}`,
						lightboxNavigation: undefined
					}
				}).route;
			}
		}

		closeFormLightbox();
		await nextTick();
		router.value.push(destinationRoute);

		// Update global store
		globalStore.clearCurrentView();
		if (destinationRoute.name === 'entries')
			nextTick(() => globalStore.setCurrentView(`${formId}-${destinationRoute.params.entryView.split('-')[0]}`));
	};

	const open = (
		view: UserEntryViewMeta,
		currentRouter: VueRouter,
		afterCloseRoute: Route
	) => {
		const formId = view.Id.split('-')[0];
		const form = globalStore.forms[formId];

		state.currentView = view;
		router.value = currentRouter;
		state.afterCloseRoute = afterCloseRoute;
		state.submitted = false;
		state.afterSubmitView = form.EntryViews.find(v => v.Id === view.AfterSubmitViewId) ?? null;

		openFormLightbox(null, formId, view.Token, view.Id, view.RoleId, form.Name, undefined, undefined, onLightboxSubmit, onLightboxClose, onBeforeLightboxSubmit, false);
	};

	return {
		openFormViewLightbox: open,
		closeFormViewLightbox: closeFormLightbox,
		adminApi
	};
};

export type FormViewLightboxOptions = ReturnType<typeof useFormViewLightbox>;